<template>
  <div class="route-map-wrapper">
    <div v-if="loading" class="map-overlay">
      <v-progress-circular indeterminate color="primary" />
      <span class="ml-2">Calcul de l'itinéraire…</span>
    </div>
    <div v-if="error" class="map-overlay map-error">
      <v-icon color="error">mdi-alert-circle-outline</v-icon>
      <span class="ml-2">{{ error }}</span>
    </div>
    <div ref="mapContainer" class="map-container" />
  </div>
</template>

<script setup lang="ts">
import type { Address } from "@/types/address";
import type { EndAddress } from "@/types/end-address";
import type { Task } from "@/types/task";
import { geocodeAddressesBatch } from "@/utils/geocode-api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps<{
  startAddress: Address;
  endAddress: EndAddress;
  tasks: Task[];
}>();

const mapContainer = ref<HTMLElement | null>(null);
const loading = ref(false);
const error = ref("");

let mapInstance: L.Map | null = null;

function formatStart(a: Address): string {
  return `${a.numero} ${a.rue}, ${a.codepostale} ${a.ville}`;
}

function formatEnd(a: EndAddress): string {
  return `${a.number} ${a.street}, ${a.postcode} ${a.city}`;
}

function formatTask(t: Task): string {
  return `${t.number ?? ""} ${t.street ?? ""}, ${t.postcode ?? ""} ${t.city ?? ""}`.trim();
}

function formatTaskFallback(t: Task): string {
  return `${t.postcode ?? ""} ${t.city ?? ""}`.trim();
}

function formatStartFallback(a: Address): string {
  return `${a.codepostale} ${a.ville}`.trim();
}

function formatEndFallback(a: EndAddress): string {
  return `${a.postcode} ${a.city}`.trim();
}

function makeIcon(label: string, color: string): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<div style="
      background:${color};
      color:#fff;
      border-radius:50%;
      width:26px;
      height:26px;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:12px;
      font-weight:bold;
      border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,.4);
    ">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function getMarkerPosition(
  coordinate: [number, number],
  markerCountByCoord: Map<string, number>
): [number, number] {
  const key = `${coordinate[0].toFixed(6)},${coordinate[1].toFixed(6)}`;
  const markerCount = markerCountByCoord.get(key) ?? 0;
  markerCountByCoord.set(key, markerCount + 1);

  if (markerCount === 0) {
    return coordinate;
  }

  const offset = 0.00012;
  const angle = markerCount * (Math.PI / 3);
  return [
    coordinate[0] + Math.cos(angle) * offset,
    coordinate[1] + Math.sin(angle) * offset,
  ];
}

async function buildMap() {
  if (!mapContainer.value) return;

  loading.value = true;
  error.value = "";

  // Destroy existing instance
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }

  // Init map
  mapInstance = L.map(mapContainer.value, { zoomControl: true });

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(mapInstance);

  // Collect stops: start → tasks in rank order → end
  type Stop = {
    label: string;
    address: string;
    fallbackAddress?: string;
    color: string;
    strikethrough?: boolean;
    name?: string;
    coordinates?: [number, number];
  };
  const stops: Stop[] = [
    {
      label: "D",
      address: formatStart(props.startAddress),
      fallbackAddress: formatStartFallback(props.startAddress),
      color: "#43a047",
    },
    ...props.tasks
      .slice()
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .map((t, i) => ({
        label: String(i + 1),
        address: formatTask(t),
        fallbackAddress: formatTaskFallback(t),
        color: t.taskStatus === "FAITE" ? "#9e9e9e" : "#1565c0",
        strikethrough: t.taskStatus === "FAITE",
        name: t.name,
        coordinates:
          typeof t.latitude === "number" && typeof t.longitude === "number"
            ? ([t.latitude, t.longitude] as [number, number])
            : undefined,
      })),
    {
      label: "A",
      address: formatEnd(props.endAddress),
      fallbackAddress: formatEndFallback(props.endAddress),
      color: "#e53935",
    },
  ];

  // Geocode stops missing coordinates via one backend batch call
  const coords: ([number, number] | null)[] = stops.map((stop) => stop.coordinates ?? null);
  const missingStopIndexes: number[] = [];
  const missingStopAddresses: string[] = [];

  stops.forEach((stop, index) => {
    if (!coords[index]) {
      missingStopIndexes.push(index);
      missingStopAddresses.push(stop.address);
    }
  });

  if (missingStopAddresses.length > 0) {
    const geocodedMissingStops = await geocodeAddressesBatch(missingStopAddresses);
    geocodedMissingStops.forEach((coordinate, batchIndex) => {
      const stopIndex = missingStopIndexes[batchIndex];
      if (typeof stopIndex === "number") {
        coords[stopIndex] = coordinate;
      }
    });
  }

  // Ensure start/end are still visible if geocoding fails by falling back to nearest known stop
  const firstTaskIndex = stops.findIndex((s) => s.label === "1");
  const lastTaskIndex = stops.findLastIndex((s) => /^\d+$/.test(s.label));
  const firstTaskCoord = firstTaskIndex >= 0 ? coords[firstTaskIndex] : null;
  const lastTaskCoord = lastTaskIndex >= 0 ? coords[lastTaskIndex] : null;

  if (!coords[0]) {
    coords[0] = firstTaskCoord ?? coords[coords.length - 1] ?? null;
  }

  if (!coords[coords.length - 1]) {
    coords[coords.length - 1] = lastTaskCoord ?? coords[0] ?? null;
  }

  const validPoints: L.LatLngTuple[] = [];
  const markerCountByCoord = new Map<string, number>();

  stops.forEach((stop, i) => {
    const c = coords[i];
    if (!c) return;
    validPoints.push(c);
    const markerPosition = getMarkerPosition(c, markerCountByCoord);

    const title = stop.name
      ? `<b style="${stop.strikethrough ? "text-decoration:line-through" : ""}">${stop.name}</b><br><small>${stop.address}</small>`
      : `<b>${stop.label === "D" ? "Départ" : "Arrivée"}</b><br><small>${stop.address}</small>`;

    L.marker(markerPosition, {
      icon: makeIcon(stop.label, stop.color),
      zIndexOffset: stop.label === "D" || stop.label === "A" ? 1000 : 0,
    })
      .addTo(mapInstance!)
      .bindPopup(title);
  });

  const startPoint = coords[0] as L.LatLngTuple | null;
  const endPoint = coords[coords.length - 1] as L.LatLngTuple | null;
  const taskPoints = coords.slice(1, Math.max(1, coords.length - 1)).filter(
    (point): point is [number, number] => point !== null
  );

  const routePoints: L.LatLngTuple[] = [];
  if (startPoint && taskPoints.length > 0) {
    routePoints.push(startPoint);
  }
  routePoints.push(...taskPoints);
  if (endPoint && taskPoints.length > 0) {
    routePoints.push(endPoint);
  }

  // Draw route polyline
  if (routePoints.length >= 2) {
    L.polyline(routePoints, { color: "#1565c0", weight: 3, opacity: 0.75, dashArray: "6 4" })
      .addTo(mapInstance!);
    mapInstance!.fitBounds(L.latLngBounds(validPoints), { padding: [30, 30] });
  } else if (validPoints.length === 1) {
    mapInstance!.setView(validPoints[0], 13);
  } else {
    // Fallback: France centre
    mapInstance!.setView([46.603354, 1.888334], 6);
    error.value = "Impossible de géocoder les adresses.";
  }

  loading.value = false;
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  buildMap();
});

watch(
  () => [props.tasks, props.startAddress, props.endAddress],
  () => buildMap(),
  { deep: true }
);

onBeforeUnmount(() => {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
});
</script>

<style scoped>
.route-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 260px;
}

.map-container {
  width: 100%;
  height: 100%;
  min-height: 260px;
  z-index: 0;
}

.map-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.85);
  padding: 8px 16px;
  border-radius: 8px;
  pointer-events: none;
}

.map-error {
  color: #b71c1c;
}
</style>
